import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { withTheme } from 'styled-components';
import { Checkbox } from 'shared-components';
import { get, post } from 'shared-components/src/utils/http';
import { assignSafeUrl } from 'shared-components/src/utils/safeUrl';
import auth from 'shared-components/src/utils/auth';
import { verifyRegisterValidations } from 'shared-components/src/validations/cardholderValidations';
import { resolveCardholderFooterLinks } from 'shared-components/src/utils/footerLinks';
import ReCAPTCHA from '../../../../components/ReCaptcha';
import { authEn } from '../../../../i18n/auth';
import { getCurrentAccount } from '../../../../utils/accounts';
import { pinRuleViolation } from '../../../../utils/pinRules';
import {
  digitsOnly,
  isAmex,
  expectedCvvLength,
  formatCardNumber,
  expandExpiry,
} from '../../../../utils/cardFormatting';
import { cardFieldError } from '../../../../utils/validationHandlers';
import PwdEyeButton from '../components/PwdEyeButton';
import CardFields from '../components/CardFields';
import CardholderAgreementLink from '../components/CardholderAgreementLink';
import PrivacyPolicyAgreementLink from '../components/PrivacyPolicyAgreementLink';
import ElectronicComConsentLink from '../components/ElectronicComConsentLink';
import {
  HeaderNav,
  HeaderLink,
  LanguageToggle,
  FormContainer,
  InputLabel,
  Input,
  InputWrapper,
  InputRow,
  ErrorMessage,
  FieldErrorMessage,
} from '../CamsLogin/styles';
import {
  PageShell,
  Header,
  MainContent,
  Wrapper,
  Title,
  Subtitle,
  Button,
  Spinner,
  TextLink,
  PageFooter,
  FooterLinks,
  FooterCopyright,
  StepCounter,
  StepBar,
  StepSegment,
  Section,
  Checklist,
  ChecklistItem,
  Note,
  LogoWrap,
  HeaderLogo,
  CardSummary,
  AgreementRow,
  CaptchaWrapper,
  FieldHint,
  PasswordFieldWrapper,
  RuleList,
  Rule,
  SecondaryButton,
  NoticeBanner,
} from './styles';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INTRO = 'intro';
const CARD = 'card';
const ACTIVATE = 'activate';
const CREDENTIALS = 'credentials';
const SET_PIN = 'set-pin';
const DONE = 'done';

// Activation is a branch off the card step rather than a step of its own, so a cardholder who
// never needs it does not see a five-step flow. The optional PIN step is the same: it is only
// offered to some programmes, so it counts as part of creating the account.
const STEP_ORDER = [INTRO, CARD, CREDENTIALS, DONE];
const BRANCH_OF = { [ACTIVATE]: CARD, [SET_PIN]: CREDENTIALS };
const stepNumber = step => STEP_ORDER.indexOf(BRANCH_OF[step] || step) + 1;

// A PIN is four digits, and the fields hold nothing else.
const PIN_LENGTH = 4;
const toPinEntry = value => String(value || '').replace(/\D/g, '').slice(0, PIN_LENGTH);

// Galileo takes PIN entry on its own hosted form and redirects back to this page with the
// outcome in the query string. The return is a fresh page load, so the step state is gone and
// the query is all there is to go on.
const readPinReturn = () => {
  try {
    const query = new URLSearchParams(window.location.search);
    const state = query.get('set_pin_state');
    if (state !== 'success' && state !== 'failure') return null;
    return {
      state,
      accountId: query.get('account_id'),
      lastFour: query.get('last_four_digits'),
    };
  } catch (e) {
    return null;
  }
};

// Re-exported so existing imports from this module keep working.
export { digitsOnly, isAmex, expectedCvvLength, formatCardNumber };

// The shared card fields drawn with this page's own inputs, so the card step behaves exactly as
// registration and activation do.
/* eslint-disable react/prop-types */
const RbcCardField = ({ id, label, children }) => (
  <InputWrapper>
    <InputLabel htmlFor={id}>{label}</InputLabel>
    {children}
  </InputWrapper>
);
/* eslint-enable react/prop-types */

const rbcCardLayout = {
  Field: RbcCardField,
  Row: InputRow,
  Input,
  PasswordWrapper: PasswordFieldWrapper,
  Error: FieldErrorMessage,
  Hint: FieldHint,
};

const RBC_CARD_IDS = { cardNumber: 'rbc-card-number', cvv: 'rbc-card-cvv', expiry: 'rbc-card-expiry' };

// Submit buttons spin while their request is in flight and stay disabled throughout, so a
// cardholder cannot fire a second verification or registration.
const SubmitButton = ({ loading, disabled, children }) => (
  <Button type="submit" disabled={disabled || loading} aria-busy={loading}>
    {loading && (
      <Spinner viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path
          opacity=".25"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 19a7 7 0 1 0 0-14 7 7 0 0 0 0 14m0 3c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10"
          fill="currentColor"
        />
        <path
          d="M12 22c5.523 0 10-4.477 10-10h-3a7 7 0 0 1-7 7zM2 12C2 6.477 6.477 2 12 2v3a7 7 0 0 0-7 7z"
          fill="currentColor"
        />
      </Spinner>
    )}
    {children}
  </Button>
);

SubmitButton.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

SubmitButton.defaultProps = {
  loading: false,
  disabled: false,
};

const RbcOnboarding = ({ theme, locale, setLocale }) => {
  const intl = useIntl();
  const brand = theme?.brand || {};
  const currentLocale = locale || 'en';
  const toggleLocale = currentLocale === 'en' ? 'fr' : 'en';

  // Read once: a Galileo return lands straight on the done step while the PIN is committed.
  const [pinReturn] = useState(readPinReturn);
  const [step, setStep] = useState(pinReturn ? DONE : INTRO);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const [card, setCard] = useState({ cardNumber: '', expiry: '', cvv: '' });
  const [cardErrors, setCardErrors] = useState({});

  const [verified, setVerified] = useState({
    lastFour: '',
    emailEnabled: false,
  });

  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    passwordConfirmation: '',
    email: '',
  });
  const [credentialErrors, setCredentialErrors] = useState({});

  // Optional PIN step, offered when the register response says so. Registration hands back a
  // signed-in session, which the PIN calls need; the cardholder is signed out again once the
  // step is finished or skipped.
  const [pinAccount, setPinAccount] = useState(null);
  const [isGalileo, setIsGalileo] = useState(false);
  const [pin, setPin] = useState({ value: '', confirmation: '' });
  const [pinError, setPinError] = useState(null);
  const [pinOutcome, setPinOutcome] = useState(null);
  const [finishingPin, setFinishingPin] = useState(Boolean(pinReturn));

  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // Masked fields default to hidden, with a toggle so a cardholder can check what they typed.
  const [passwordType, setPasswordType] = useState('password');
  const [confirmType, setConfirmType] = useState('password');

  // Used to move focus to the field an error belongs to, so the cardholder does not have to
  // hunt for it -- particularly on the username, where "already taken" is the common case.
  const fieldRefs = useRef({});
  const registerField = name => el => {
    fieldRefs.current[name] = el;
  };
  const focusField = name => {
    if (fieldRefs.current[name]) fieldRefs.current[name].focus();
  };

  const [agreed, setAgreed] = useState({
    cardholderAgreement: false,
    privacyPolicy: false,
    electronicConsent: false,
    terms: false,
  });

  // The page is served from the same origin as the portal, so a relative path keeps the
  // cardholder on this host. The chosen language survives in sessionStorage -- no ?lang= hop
  // is needed the way it is for the cross-domain CAMS links.
  const loginUrl = '/login';

  // Keep submit disabled until there is something to submit. The rules themselves are checked
  // on blur and again on submit, so this only guards against an empty round trip.
  const credentialsComplete =
    credentials.username &&
    credentials.password &&
    credentials.passwordConfirmation &&
    (!verified.emailEnabled || credentials.email);

  // Agreements are only shown when the programme has both language variants configured,
  // matching the standalone activation page.
  const needsCardholderAgreement = Boolean(
    brand.activateCardholderAgreementEn && brand.activateCardholderAgreementFr
  );
  const needsPrivacyPolicy = Boolean(
    brand.activatePrivacyPolicyEn && brand.activatePrivacyPolicyFr
  );
  const needsElectronicConsent = Boolean(
    brand.activateElectronicComConsentEn && brand.activateElectronicComConsentFr
  );
  const needsGenericTerms = !needsCardholderAgreement;

  const allAgreementsAccepted =
    (!needsCardholderAgreement || agreed.cardholderAgreement) &&
    (!needsPrivacyPolicy || agreed.privacyPolicy) &&
    (!needsElectronicConsent || agreed.electronicConsent) &&
    (!needsGenericTerms || agreed.terms);

  const handleLocaleChange = () => {
    if (setLocale) {
      sessionStorage.setItem('locale', toggleLocale);
      setLocale(toggleLocale);
    }
  };

  const resetCaptcha = () => {
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setCaptchaToken(null);
  };

  const message = key => intl.messages[key];

  // Errors are held in state as message keys, never as resolved text: a resolved string is
  // frozen in the language it was produced in, so switching language would leave the old
  // wording on screen. Anything without a key (a raw API message we cannot translate) is
  // wrapped as { raw }.
  const errorText = entry => {
    if (!entry) return '';
    return typeof entry === 'string' ? message(entry) || '' : entry.raw || '';
  };

  // Pick the key for an API error code, falling back to the untranslatable message it sent.
  const apiErrorEntry = error => {
    const key = `errors-${error.code}`;
    if (intl.messages[key]) return key;
    return error.message ? { raw: error.message } : 'rbc-error-generic';
  };

  // One rule per field, shared by the blur handlers and by submit, so a field can never pass
  // on blur and then fail on submit with different wording. Each returns '' when valid. The
  // card rules are the shared card fields' (cardFieldError), as on registration and activation.
  const credentialFieldError = (field, values = credentials) => {
    switch (field) {
    case 'username':
      if (!values.username) return 'errors-username-required';
      return verifyRegisterValidations.username(values.username)
        ? ''
        : 'errors-username-invalid';
    case 'password':
      if (!values.password) return 'errors-password-required';
      return verifyRegisterValidations.password(values.password)
        ? ''
        : 'errors-password-invalid';
    case 'passwordConfirmation':
      if (!values.passwordConfirmation) return 'errors-password-confirmation-required';
      return values.password === values.passwordConfirmation
        ? ''
        : 'errors-password-confirmation-invalid';
    case 'email':
      if (!verified.emailEnabled) return '';
      if (!values.email) return 'errors-email-required';
      return EMAIL_REGEX.test(values.email) ? '' : 'errors-email-invalid';
    default:
      return '';
    }
  };

  // Validate on blur so a mistake is caught while the cardholder is still on the field,
  // rather than only when they submit.
  const handleCardBlur = field =>
    setCardErrors(prev => ({ ...prev, [field]: cardFieldError(field, card) }));

  const handleCredentialBlur = field =>
    setCredentialErrors(prev => ({ ...prev, [field]: credentialFieldError(field) }));

  const collectErrors = (fields, resolver) =>
    fields.reduce((acc, field) => {
      const error = resolver(field);
      return error ? { ...acc, [field]: error } : acc;
    }, {});

  const validateCard = () => {
    const errors = collectErrors(['cardNumber', 'expiry', 'cvv'], field => cardFieldError(field, card));
    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCredentials = () => {
    const errors = collectErrors(
      ['username', 'password', 'passwordConfirmation', 'email'],
      credentialFieldError
    );
    setCredentialErrors(errors);

    const [firstInvalid] = Object.keys(errors);
    if (firstInvalid) focusField(firstInvalid);

    return Object.keys(errors).length === 0;
  };

  // Live feedback for the password rules. Derived from the same regex the API enforces
  // (validatePassword), including the lowercase requirement.
  const passwordRules = [
    { key: 'length', label: message('errors-password-length'), met: credentials.password.length >= 12 },
    { key: 'case', label: message('errors-password-case'), met: /[A-Z]/.test(credentials.password) },
    {
      key: 'lowercase',
      label: message('rbc-password-rule-lowercase'),
      met: /[a-z]/.test(credentials.password),
    },
    { key: 'number', label: message('errors-password-number'), met: /\d/.test(credentials.password) },
    {
      key: 'symbol',
      label: message('errors-password-symbol'),
      met: /[~@#$%^&*+=`|{}:;!.?"()[\]-]/.test(credentials.password),
    },
  ];

  const parseError = res => ({
    code: res?.error?.code || 'unknown_error',
    message: res?.error?.message,
  });

  // API field errors are merged into whatever is already on screen and the field is focused,
  // rather than replacing the error map wholesale.
  const setCardFieldError = (field, entry) => {
    setCardErrors(prev => ({ ...prev, [field]: entry }));
    focusField(field);
  };

  const setCredentialFieldError = (field, entry) => {
    setCredentialErrors(prev => ({ ...prev, [field]: entry }));
    focusField(field);
  };

  // An unactivated card is a branch in the flow, never an unknown-card error: newly issued
  // cards in this programme are inactive until activated, so this is a common path.
  const handleVerifyError = error => {
    switch (error.code) {
    case 'card_not_activated':
      setStep(ACTIVATE);
      return;
    case 'cardholder_already_registered':
      setAlreadyRegistered(true);
      setFormError('rbc-card-already-registered');
      return;
    case 'invalid_card_number':
      setCardFieldError('cardNumber', `errors-${error.code}`);
      return;
    case 'failed_to_verify_card':
    case 'failed_to_verify_card_cvv':
      setCardFieldError('cvv', `errors-${error.code}`);
      return;
    case 'failed_to_verify_card_expiry_date':
    case 'failed_to_verify_card_expired':
      setCardFieldError('expiry', `errors-${error.code}`);
      return;
    default:
      setFormError(apiErrorEntry(error));
    }
  };

  // Returns true when the card is confirmed and we hold a registration session.
  const verifyCard = async token => {
    // The card fields take the expiry as MM/YY; the API wants a four-digit year. Split the
    // digits rather than matching the formatted value: the format is already enforced by
    // validation, and a regex that fails here would post an undefined month.
    const expiryDigits = expandExpiry(card.expiry);
    const expiryMonth = expiryDigits.slice(0, 2);
    const expiryYear = expiryDigits.slice(2, 6);

    const res = await post('/api/v2/cardholder_portal/register/verify', {
      // The field is grouped for readability, so send digits only.
      card_number: digitsOnly(card.cardNumber),
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
      cvv: card.cvv,
      recaptcha_token: token,
    });

    if (res.error) throw parseError(res);

    setVerified({
      lastFour: digitsOnly(card.cardNumber).slice(-4),
      emailEnabled: Boolean(res.data.registration_success_email_enabled),
    });
    if (res.data.email) {
      setCredentials(prev => ({ ...prev, email: res.data.email }));
    }
    return true;
  };

  const handleConfirmCard = async e => {
    e.preventDefault();
    if (loading) return;
    setFormError(null);
    if (!validateCard() || !captchaToken) return;

    setLoading(true);
    try {
      await verifyCard(captchaToken);
      setStep(CREDENTIALS);
    } catch (error) {
      handleVerifyError(error);
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  // Activate, then confirm the card again so the flow continues without the cardholder
  // re-entering anything. The second confirmation needs a fresh captcha token because the
  // first one is spent.
  const handleActivate = async e => {
    e.preventDefault();
    if (loading) return;
    setFormError(null);
    if (!captchaToken) return;

    // The card fields take the expiry as MM/YY; the API wants a four-digit year. Split the
    // digits rather than matching the formatted value: the format is already enforced by
    // validation, and a regex that fails here would post an undefined month.
    const expiryDigits = expandExpiry(card.expiry);
    const expiryMonth = expiryDigits.slice(0, 2);
    const expiryYear = expiryDigits.slice(2, 6);

    setLoading(true);
    try {
      const res = await post('/api/v1/cardholder_portal/activate_card', {
        card_number: digitsOnly(card.cardNumber),
        cvv: card.cvv,
        expiry_month: expiryMonth,
        expiry_year: expiryYear,
      });

      if (res.error) throw parseError(res);

      await verifyCard(captchaToken);
      setStep(CREDENTIALS);
    } catch (error) {
      if (error.code === 'card_not_activated') {
        setFormError('rbc-error-activation-failed');
      } else {
        handleVerifyError(error);
      }
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  // Deliberately no lasting sign-in: the cardholder signs in themselves, which confirms the
  // credentials work and keeps any two-factor setup on the normal login path. Registration
  // sets session cookies, so the server is asked to clear them as well as the local expiry.
  const endSession = async () => {
    try {
      await get('/api/v1/cardholder_portal/logout');
    } catch (e) {
      // Still signed out locally; the cookies expire on their own.
    }
    await auth.logout();
  };

  // Loads what the PIN step needs to submit. The step is optional, so anything missing or
  // failing here skips it rather than stranding the cardholder.
  const preparePinStep = async () => {
    try {
      const [cardholderRes, processorRes] = await Promise.all([
        get('/api/v1/cardholder_portal/cardholder'),
        get('/api/v1/cardholder_portal/cardholder/program_processor'),
      ]);
      const account = getCurrentAccount(cardholderRes.data);
      if (!account || !account.cards || !account.cards.length) return false;

      setPinAccount({ id: account.id, lastFour: account.cards[0].last_four_digits });
      setIsGalileo(processorRes.data?.processor_name === 'galileo');
      return true;
    } catch (e) {
      return false;
    }
  };

  const finishPinStep = async outcome => {
    setLoading(true);
    await endSession();
    setPinOutcome(outcome);
    setLoading(false);
    setStep(DONE);
  };

  // Back from Galileo's hosted form: the PIN is only staged there and takes a commit to land.
  // Either way the cardholder is then signed out and shown the done step.
  useEffect(() => {
    if (!pinReturn) return;

    // Drop the query so a refresh does not try to commit the same PIN again.
    window.history.replaceState(null, '', window.location.pathname);

    const finish = async () => {
      let outcome = 'failed';
      if (pinReturn.state === 'success') {
        try {
          await post(
            `/api/v1/cardholder_portal/cardholder/accounts/${pinReturn.accountId}/pin/commit`,
            { last_four_digits: pinReturn.lastFour }
          );
          outcome = 'set';
        } catch (e) {
          // Reported on the done step; the PIN can still be set from the profile.
        }
      }
      await endSession();
      setPinOutcome(outcome);
      setFinishingPin(false);
    };
    finish();
    // Runs once: the Galileo return is a fresh page load, not a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetPin = async e => {
    e.preventDefault();
    if (loading) return;
    setPinError(null);

    if (isGalileo) {
      setLoading(true);
      try {
        const base = `${window.location.origin}${window.location.pathname}`;
        const params = `account_id=${pinAccount.id}&last_four_digits=${pinAccount.lastFour}`;
        const {
          data: { url },
        } = await post(
          `/api/v1/cardholder_portal/cardholder/accounts/${pinAccount.id}/pin/direct_render`,
          {
            last_four_digits: pinAccount.lastFour,
            // Galileo renders its hosted form in this locale.
            locale: currentLocale,
            callback_url: `${base}?set_pin_state=success&${params}`,
            failure_callback_url: `${base}?set_pin_state=failure&${params}`,
          }
        );
        // Only an http(s) URL is followed; anything else is reported like a failed request.
        if (!assignSafeUrl(url)) throw new Error('Unsafe Set PIN URL');
      } catch (error) {
        if (error.status === 401) {
          await finishPinStep('failed');
          return;
        }
        setPinError('register-set-pin-failed');
        setLoading(false);
      }
      return;
    }

    if (pin.value.length !== PIN_LENGTH || pin.confirmation.length !== PIN_LENGTH) {
      setPinError('register-set-pin-invalid');
      return;
    }
    if (pin.value !== pin.confirmation) {
      setPinError('register-set-pin-mismatch');
      return;
    }
    const violation = pinRuleViolation(pin.value, pinAccount.lastFour);
    if (violation) {
      setPinError(`register-set-pin-${violation}`);
      return;
    }

    setLoading(true);
    try {
      await post(`/api/v1/cardholder_portal/cardholder/accounts/${pinAccount.id}/set_pin`, {
        last_four_digits: pinAccount.lastFour,
        pin: pin.value,
      });
      await finishPinStep('set');
    } catch (error) {
      // An expired session cannot be fixed from here; the PIN can be set after signing in.
      if (error.status === 401) {
        await finishPinStep('failed');
        return;
      }
      // Anything else is most likely the processor refusing the PIN. Its reason does not
      // survive the trip back (a bare "Failed to send request."), so point at the requirements.
      setPinError('register-set-pin-rejected');
      setLoading(false);
    }
  };

  const handleCreateAccount = async e => {
    e.preventDefault();
    if (loading) return;
    setFormError(null);
    if (!validateCredentials()) return;

    const payload = {
      username: credentials.username,
      password: credentials.password,
      password_confirmation: credentials.passwordConfirmation,
      last_four_digits: verified.lastFour,
    };
    if (verified.emailEnabled) payload.email = credentials.email;

    setLoading(true);
    try {
      const res = await post('/api/v2/cardholder_portal/register', payload);
      if (res.error) throw parseError(res);

      // The PIN step runs on the session registration just opened, so it has to come before
      // signing out.
      if (res.set_pin_offered && (await preparePinStep())) {
        setStep(SET_PIN);
        return;
      }

      await endSession();
      setStep(DONE);
    } catch (error) {
      switch (error.code) {
      // The card can already have an account by the time this step is submitted -- either it
      // was registered in another tab or window, or it slipped past the card step. Nothing on
      // this form can fix that, so point the cardholder at the login page instead of leaving
      // them retrying a submit that cannot succeed.
      case 'cardholder_already_registered':
        setAlreadyRegistered(true);
        setFormError('rbc-card-already-registered');
        break;
      case 'username_not_available':
        setCredentialFieldError('username', `errors-${error.code}`);
        break;
      case 'invalid_password':
        setCredentialFieldError('password', `errors-${error.code}`);
        break;
      case 'invalid_email':
      case 'email_already_exists':
        setCredentialFieldError('email', `errors-${error.code}`);
        break;
      case 'invalid_register_token':
      case 'register_token_expired':
        setFormError('errors-register_token_expired');
        setStep(CARD);
        break;
      default:
        setFormError(apiErrorEntry(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderCaptcha = () => (
    <CaptchaWrapper>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        onChange={token => {
          setCaptchaToken(token);
          setFormError(null);
        }}
        onExpired={() => setCaptchaToken(null)}
        onErrored={() => {
          setCaptchaToken(null);
          setFormError('errors-recaptcha_timeout');
          resetCaptcha();
        }}
      />
    </CaptchaWrapper>
  );

  const renderError = () =>
    formError && (
      <>
        <ErrorMessage role="alert" aria-live="polite">
          <span>{errorText(formError)}</span>
        </ErrorMessage>
        {/* Nothing on the form can resolve an already-registered card, so signing in becomes
            the primary action. Rendered as a link styled as a button: it navigates. */}
        {alreadyRegistered && (
          <Button as="a" href={loginUrl}>
            {message('rbc-done-signin')}
          </Button>
        )}
      </>
    );

  const renderIntro = () => (
    <>
      {/* Migration notice (DH-2772), landing step only. role="status" rather than "alert":
          it is standing information the cardholder should hear when the page is announced,
          not an interruption worth pre-empting whatever they are reading. */}
      <NoticeBanner role="status">
        <strong>{message('rbc-intro-notice-heading')}</strong>{' '}
        {message('rbc-intro-notice-body')}
      </NoticeBanner>

      <Title>{message('rbc-intro-title')}</Title>
      <Subtitle>{message('rbc-intro-subtitle')}</Subtitle>

      <Section>
        <h3>{message('rbc-intro-what-heading')}</h3>
        <p>{message('rbc-intro-what-body')}</p>
      </Section>

      <Section>
        <h3>{message('rbc-intro-need-heading')}</h3>
        <Checklist>
          <ChecklistItem>{message('rbc-intro-need-card')}</ChecklistItem>
          <ChecklistItem>{message('rbc-intro-need-email')}</ChecklistItem>
        </Checklist>
      </Section>

      <Note>{message('rbc-intro-time')}</Note>

      <Button type="button" onClick={() => setStep(CARD)}>
        {message('rbc-intro-continue')}
      </Button>
      <TextLink href={loginUrl}>{message('rbc-intro-have-account')}</TextLink>
    </>
  );

  const renderCardStep = () => (
    <>
      <Title>{message('rbc-card-title')}</Title>
      <Subtitle>{message('rbc-card-subtitle')}</Subtitle>

      <FormContainer onSubmit={handleConfirmCard}>
        <CardFields
          values={card}
          errors={cardErrors}
          onChange={(field, value) => {
            setCard(prev => ({ ...prev, [field]: value }));
            setCardErrors(prev => ({ ...prev, [field]: null }));
          }}
          onBlur={handleCardBlur}
          disabled={loading}
          layout={rbcCardLayout}
          ids={RBC_CARD_IDS}
          inputRef={registerField}
          errorText={errorText}
          cvvHint={intl.formatMessage(
            { id: 'rbc-card-cvv-hint' },
            { length: expectedCvvLength(digitsOnly(card.cardNumber)) }
          )}
        />

        {renderCaptcha()}

        {renderError()}
        
        <SubmitButton loading={loading} disabled={!captchaToken || alreadyRegistered}>
          {message('rbc-card-submit')}
        </SubmitButton>

        <SecondaryButton type="button" onClick={() => setStep(INTRO)} disabled={loading}>
          {message('rbc-card-back')}
        </SecondaryButton>
      </FormContainer>
    </>
  );

  const renderActivateStep = () => (
    <>
      <Title>{message('rbc-activate-title')}</Title>
      <Subtitle>{message('rbc-activate-subtitle')}</Subtitle>

      <CardSummary>
        <strong>
          {intl.formatMessage(
            { id: 'rbc-activate-card-summary' },
            { lastFour: digitsOnly(card.cardNumber).slice(-4) }
          )}
        </strong>
        <span>{message('rbc-activate-no-reentry')}</span>
      </CardSummary>

      <FormContainer onSubmit={handleActivate}>
        {needsElectronicConsent && (
          <AgreementRow>
            <Checkbox
              label={
                <FormattedMessage
                  id="activate-electronic-com-consent-label"
                  defaultMessage={authEn['activate-electronic-com-consent-label']}
                  values={{ link: <ElectronicComConsentLink /> }}
                />
              }
              labelFor="rbcElectronicConsent"
              id="rbc-electronic-consent"
              value={agreed.electronicConsent}
              onChange={e =>
                setAgreed(prev => ({ ...prev, electronicConsent: e.target.checked }))
              }
            />
          </AgreementRow>
        )}
        {needsCardholderAgreement && (
          <AgreementRow>
            <Checkbox
              label={
                <FormattedMessage
                  id="activate-cardholder-agreement-label"
                  defaultMessage={authEn['activate-cardholder-agreement-label']}
                  values={{ link: <CardholderAgreementLink /> }}
                />
              }
              labelFor="rbcCardholderAgreement"
              id="rbc-cardholder-agreement"
              value={agreed.cardholderAgreement}
              onChange={e =>
                setAgreed(prev => ({ ...prev, cardholderAgreement: e.target.checked }))
              }
            />
          </AgreementRow>
        )}
        {needsPrivacyPolicy && (
          <AgreementRow>
            <Checkbox
              label={
                <FormattedMessage
                  id="activate-privacy-policy-label"
                  defaultMessage={authEn['activate-privacy-policy-label']}
                  values={{ link: <PrivacyPolicyAgreementLink /> }}
                />
              }
              labelFor="rbcPrivacyPolicy"
              id="rbc-privacy-policy"
              value={agreed.privacyPolicy}
              onChange={e => setAgreed(prev => ({ ...prev, privacyPolicy: e.target.checked }))}
            />
          </AgreementRow>
        )}
        {needsGenericTerms && (
          <AgreementRow>
            <Checkbox
              label={message('activate-terms-consent-label')}
              labelFor="rbcTermsConsent"
              id="rbc-terms-consent"
              value={agreed.terms}
              onChange={e => setAgreed(prev => ({ ...prev, terms: e.target.checked }))}
            />
          </AgreementRow>
        )}

        <Note>{message('rbc-activate-captcha-note')}</Note>
        {renderCaptcha()}

        <SubmitButton loading={loading} disabled={!captchaToken || !allAgreementsAccepted}>
          {message('rbc-activate-submit')}
        </SubmitButton>
        {renderError()}
      </FormContainer>
    </>
  );

  const renderCredentialsStep = () => (
    <>
      <Title>{message('rbc-login-title')}</Title>
      <Subtitle>{message('rbc-login-subtitle')}</Subtitle>

      <FormContainer onSubmit={handleCreateAccount}>
        <InputWrapper>
          <InputLabel htmlFor="rbc-username">{message('rbc-login-username-label')}</InputLabel>
          <Input
            id="rbc-username"
            ref={registerField('username')}
            value={credentials.username}
            onChange={e => {
              setCredentials(prev => ({ ...prev, username: e.target.value.trim() }));
              setCredentialErrors(prev => ({ ...prev, username: null }));
            }}
            onBlur={() => handleCredentialBlur('username')}
            className={credentialErrors.username ? 'is-invalid' : ''}
            autoComplete="username"
            minLength={3}
            maxLength={30}
            aria-invalid={Boolean(credentialErrors.username)}
            aria-describedby={credentialErrors.username ? 'rbc-username-error' : 'rbc-username-hint'}
            disabled={loading}
          />
          {credentialErrors.username ? (
            <FieldErrorMessage id="rbc-username-error">{errorText(credentialErrors.username)}</FieldErrorMessage>
          ) : (
            <FieldHint id="rbc-username-hint">{message('rbc-login-username-hint')}</FieldHint>
          )}
        </InputWrapper>

        <InputWrapper>
          <InputLabel htmlFor="rbc-password">{message('rbc-login-password-label')}</InputLabel>
          <PasswordFieldWrapper>
            <Input
              id="rbc-password"
              ref={registerField('password')}
              type={passwordType}
              value={credentials.password}
              onChange={e => {
                setCredentials(prev => ({ ...prev, password: e.target.value.trim() }));
                setCredentialErrors(prev => ({ ...prev, password: null }));
              }}
              onBlur={() => handleCredentialBlur('password')}
              className={credentialErrors.password ? 'is-invalid' : ''}
              autoComplete="new-password"
              minLength={12}
              aria-invalid={Boolean(credentialErrors.password)}
              aria-describedby={credentialErrors.password ? 'rbc-password-error' : 'rbc-password-rules'}
              disabled={loading}
            />
            <PwdEyeButton
              type={passwordType}
              settype={setPasswordType}
              label={message(
                passwordType === 'password' ? 'rbc-show-password' : 'rbc-hide-password'
              )}
            />
          </PasswordFieldWrapper>
          {credentialErrors.password && (
            <FieldErrorMessage id="rbc-password-error">{errorText(credentialErrors.password)}</FieldErrorMessage>
          )}
          {/* Live rule feedback, so the cardholder is not guessing what "invalid" means. */}
          <FieldHint>{message('rbc-password-rules-heading')}</FieldHint>
          <RuleList id="rbc-password-rules">
            {passwordRules.map(rule => (
              <Rule key={rule.key} met={rule.met}>
                {rule.label}
              </Rule>
            ))}
          </RuleList>
        </InputWrapper>

        <InputWrapper>
          <InputLabel htmlFor="rbc-password-confirm">
            {message('rbc-login-passwordconfirm-label')}
          </InputLabel>
          <PasswordFieldWrapper>
            <Input
              id="rbc-password-confirm"
              ref={registerField('passwordConfirmation')}
              type={confirmType}
              value={credentials.passwordConfirmation}
              onChange={e => {
                setCredentials(prev => ({
                  ...prev,
                  passwordConfirmation: e.target.value.trim(),
                }));
                setCredentialErrors(prev => ({ ...prev, passwordConfirmation: null }));
              }}
              onBlur={() => handleCredentialBlur('passwordConfirmation')}
              className={credentialErrors.passwordConfirmation ? 'is-invalid' : ''}
              autoComplete="new-password"
              minLength={12}
              aria-invalid={Boolean(credentialErrors.passwordConfirmation)}
              aria-describedby={credentialErrors.passwordConfirmation ? 'rbc-password-confirm-error' : undefined}
              disabled={loading}
            />
            <PwdEyeButton
              type={confirmType}
              settype={setConfirmType}
              label={message(
                confirmType === 'password' ? 'rbc-show-password' : 'rbc-hide-password'
              )}
            />
          </PasswordFieldWrapper>
          {credentialErrors.passwordConfirmation && (
            <FieldErrorMessage id="rbc-password-confirm-error">{errorText(credentialErrors.passwordConfirmation)}</FieldErrorMessage>
          )}
        </InputWrapper>

        {/* The email step is governed by the same per-programme setting as the welcome email;
            with it off, asking for an address achieves nothing. */}
        {verified.emailEnabled && (
          <InputWrapper>
            <InputLabel htmlFor="rbc-email">{message('rbc-login-email-label')}</InputLabel>
            <Input
              id="rbc-email"
              ref={registerField('email')}
              type="email"
              value={credentials.email}
              onChange={e => {
                setCredentials(prev => ({ ...prev, email: e.target.value.trim() }));
                setCredentialErrors(prev => ({ ...prev, email: null }));
              }}
              onBlur={() => handleCredentialBlur('email')}
              className={credentialErrors.email ? 'is-invalid' : ''}
              autoComplete="email"
              aria-invalid={Boolean(credentialErrors.email)}
              aria-describedby={credentialErrors.email ? 'rbc-email-error' : 'rbc-email-hint'}
              disabled={loading}
            />
            {credentialErrors.email ? (
              <FieldErrorMessage id="rbc-email-error">{errorText(credentialErrors.email)}</FieldErrorMessage>
            ) : (
              <FieldHint id="rbc-email-hint">{message('rbc-login-email-hint')}</FieldHint>
            )}
          </InputWrapper>
        )}

        <SubmitButton loading={loading} disabled={!credentialsComplete || alreadyRegistered}>
          {message('rbc-login-submit')}
        </SubmitButton>
        {renderError()}
      </FormContainer>
    </>
  );

  const renderSetPinStep = () => (
    <>
      <Title>{message('register-set-pin-heading')}</Title>
      <Subtitle>
        {message(isGalileo ? 'register-set-pin-galileo-subheader' : 'register-set-pin-subheader')}
      </Subtitle>
      {/* Galileo's hosted form applies and explains its own rules. */}
      {!isGalileo && <Subtitle>{message('register-set-pin-requirements')}</Subtitle>}

      <FormContainer onSubmit={handleSetPin}>
        {!isGalileo && (
          <>
            <InputWrapper>
              <InputLabel htmlFor="rbc-pin">{message('register-set-pin-new-label')}</InputLabel>
              <Input
                id="rbc-pin"
                type="password"
                value={pin.value}
                onChange={e => {
                  const value = toPinEntry(e.target.value);
                  setPin(prev => ({ ...prev, value }));
                  setPinError(null);
                }}
                className={pinError ? 'is-invalid' : ''}
                inputMode="numeric"
                autoComplete="off"
                maxLength={PIN_LENGTH}
                aria-invalid={Boolean(pinError)}
                aria-describedby={pinError ? 'rbc-pin-error' : undefined}
                disabled={loading}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel htmlFor="rbc-pin-confirm">
                {message('register-set-pin-confirm-label')}
              </InputLabel>
              <Input
                id="rbc-pin-confirm"
                type="password"
                value={pin.confirmation}
                onChange={e => {
                  const confirmation = toPinEntry(e.target.value);
                  setPin(prev => ({ ...prev, confirmation }));
                  setPinError(null);
                }}
                className={pinError ? 'is-invalid' : ''}
                inputMode="numeric"
                autoComplete="off"
                maxLength={PIN_LENGTH}
                aria-invalid={Boolean(pinError)}
                aria-describedby={pinError ? 'rbc-pin-error' : undefined}
                disabled={loading}
              />
            </InputWrapper>
          </>
        )}

        {pinError && (
          <ErrorMessage id="rbc-pin-error" role="alert" aria-live="polite">
            <span>{errorText(pinError)}</span>
          </ErrorMessage>
        )}

        <SubmitButton loading={loading}>
          {message(isGalileo ? 'rbc-pin-galileo-submit' : 'rbc-pin-submit')}
        </SubmitButton>
        <SecondaryButton type="button" onClick={() => finishPinStep(null)} disabled={loading}>
          {message('register-set-pin-skip-button')}
        </SecondaryButton>
      </FormContainer>
    </>
  );

  // Signing in is held back until the Galileo commit and sign-out have finished, so the
  // cardholder cannot leave mid-way and arrive at the login page still signed in.
  const renderDone = () => (finishingPin ? (
    <>
      <Title>{message('rbc-done-title')}</Title>
      <Subtitle role="status">{message('rbc-done-finishing')}</Subtitle>
    </>
  ) : (
    <>
      <Title>{message('rbc-done-title')}</Title>
      {pinOutcome === 'set' && (
        <NoticeBanner role="status">{message('register-set-pin-success')}</NoticeBanner>
      )}
      {pinOutcome === 'failed' && (
        <NoticeBanner role="status">{message('register-set-pin-failed-text')}</NoticeBanner>
      )}
      <Subtitle>{message('rbc-done-body')}</Subtitle>
      <Note>{message('rbc-done-security-note')}</Note>
      <Button as="a" href={loginUrl}>
        {message('rbc-done-signin')}
      </Button>
    </>
  ));

  // Same ordered, per-language link config as the CAMS and pre-login footers, but styled from
  // theme.brand so the colours follow the white label on every host.
  const renderFooter = () => {
    const footerLinks = resolveCardholderFooterLinks(brand.cardholder_footer, currentLocale);
    const companyName = brand.copyright_company_name;

    if (!footerLinks.length && !companyName) return null;

    return (
      <PageFooter>
        {footerLinks.length > 0 && (
          <FooterLinks>
            {footerLinks.map((link, index) => (
              <React.Fragment key={link.key || link.url}>
                {index > 0 && <span aria-hidden="true">|</span>}
                <a
                  href={link.url}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                >
                  {link.title}
                </a>
              </React.Fragment>
            ))}
          </FooterLinks>
        )}
        {companyName && (
          <FooterCopyright>
            &copy; {new Date().getFullYear()} {companyName}
          </FooterCopyright>
        )}
      </PageFooter>
    );
  };

  const renderStep = () => {
    switch (step) {
    case CARD:
      return renderCardStep();
    case ACTIVATE:
      return renderActivateStep();
    case CREDENTIALS:
      return renderCredentialsStep();
    case SET_PIN:
      return renderSetPinStep();
    case DONE:
      return renderDone();
    default:
      return renderIntro();
    }
  };

  const logoUrl = currentLocale === 'fr' ? brand.logo_fr || brand.logo : brand.logo;

  return (
    <PageShell>
      <Header>
        <LogoWrap>
          {logoUrl && <HeaderLogo src={logoUrl} alt={brand.copyright_company_name || ''} />}
        </LogoWrap>
        <HeaderNav>
          {brand.cardholder_knowledge_base_url && (
            <HeaderLink
              href={brand.cardholder_knowledge_base_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {message('rbc-header-help')}
            </HeaderLink>
          )}
          <LanguageToggle onClick={handleLocaleChange}>
            {toggleLocale.toUpperCase()}
          </LanguageToggle>
        </HeaderNav>
      </Header>

      <MainContent>
        <Wrapper>
          <StepCounter>
            {intl.formatMessage(
              { id: 'rbc-step-counter' },
              { current: stepNumber(step), total: STEP_ORDER.length }
            )}
          </StepCounter>
          <StepBar>
            {STEP_ORDER.map((orderedStep, index) => (
              <StepSegment key={orderedStep} done={index < stepNumber(step)} />
            ))}
          </StepBar>

          {renderStep()}
        </Wrapper>

        {renderFooter()}
      </MainContent>
    </PageShell>
  );
};

RbcOnboarding.propTypes = {
  theme: PropTypes.shape({ brand: PropTypes.shape({}) }),
  locale: PropTypes.string,
  setLocale: PropTypes.func,
};

RbcOnboarding.defaultProps = {
  theme: { brand: {} },
  locale: 'en',
  setLocale: null,
};

export default withTheme(RbcOnboarding);
